import axios from '@/lib/axios'
import router from '@/router'
import { useGlobalErrorsStore } from '@/stores/globalErrors'
import { useHotelStore } from '@/stores/hotel'
import { useQuery } from '@tanstack/vue-query'
import { objectOmit } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import { LocationQuery, useRoute } from 'vue-router'
import { ToastInterface, useToast } from 'vue-toastification'

import { HotelDetailsResponse } from '../interfaces/hotelDetailsResponse'
import { HotelsSearchRequest } from '../interfaces/hotelsSearchRequest'

const getHotel = async (
	payload: LocationQuery,
	toast: ToastInterface,
	setErrorMessage: (message: string) => void = () => {}
): Promise<HotelDetailsResponse | void> => {
	try {
		const { data } = await axios.post<HotelDetailsResponse>(
			'/api/hotels/details',
			objectOmit(
				{
					...payload,
					users: JSON.parse(payload.users as string) as string[],
				} as HotelsSearchRequest,
				['latitude', 'longitude']
			)
		)

		// Un detalle que fue búsqueda nueva (por nombre de hotel o cambio de fechas) ya
		// quedó en el historial: la marca se cambia por su id ANTES de pintar las
		// habitaciones, que copian esta URL para ir a la tarifa. Así recargar no la vuelve
		// a contar y la reserva la puede ligar.
		if (data.searchId && payload.newSearch) {
			const { newSearch: _newSearch, ...query } =
				router.currentRoute.value.query

			await router.replace({
				query: { ...query, hotelSearchId: String(data.searchId) },
			})
		}

		return data
	} catch (err: any) {
		setErrorMessage(
			err.response.data.message || 'Error al obtener detalles del hotel'
		)
	}
}

const useHotel = () => {
	const { setHotel, setTravelers } = useHotelStore()
	const { hotel, travelers } = storeToRefs(useHotelStore())
	const { query } = useRoute()
	const toast = useToast()
	const { setError } = useGlobalErrorsStore()

	const { data, isLoading, refetch, isError, isFetching } = useQuery(
		['hotel'],
		() => getHotel(query, toast, setError),
		{
			retry: false,
			refetchOnReconnect: false,
			refetchOnWindowFocus: false,
		}
	)

	watch(
		data,
		(value) => {
			if (value) {
				setHotel(value?.hotel)
			}
		},
		{ immediate: true }
	)

	return {
		hotel,
		travelers,
		query,
		isLoading,
		isError,
		isFetching,

		refetch,
		setHotel,
		setTravelers,
	}
}

export default useHotel
